import {
  PERMIT_KINDS,
  PERMIT_STATUSES,
  type PermitKind,
  REMARK_KINDS,
  REMARK_PHOTO_EXTENSIONS,
  type RemarkKind,
  type RemarkSource,
  type SiteMyDay,
  type SiteMyDayDelivery,
  type SiteMyDayProject,
  type SitePhotoLinks,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Camera, ChevronLeft, ChevronRight, ClipboardList, PackageCheck, Pencil } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { meQuery, useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { SITE_STATES } from './model';
import {
  enqueuePendingDelivery,
  PendingDeliveriesStore,
  type PendingDelivery,
  retryPendingDelivery,
  syncPendingDeliveries,
} from './offline/pending-deliveries';
import {
  enqueuePendingRemark,
  type PendingRemark,
  PendingRemarksStore,
  type RemarkScope,
  remarkPhotoError,
  syncPendingRemarks,
} from './offline/pending-remarks';
import { siteDeviceScope, siteReportWaiting, syncLocalSiteReports } from './offline/report-sync';
import { SiteOfflineStore } from './offline/store';
import { PhotoMarkupDialog } from './PhotoMarkupDialog';
import { WatchPanel } from './WatchPanel';

type Search = { day?: string; days?: number };
const REMARK_COMMITTED = 'epc-site-remark-committed';

const todayStr = () => new Date().toLocaleDateString('en-CA');
function shift(date: string, n: number) {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const dateLabel = (date: string) =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    weekday: 'short',
    timeZone: 'UTC',
  });
const timeLabel = (iso: string) =>
  new Date(iso).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
const qty = (v: string | null) => (v === null ? '—' : v.includes('.') ? v.replace(/\.?0+$/, '') : v);

/** Тип замечания на площадке (ПР-07) → источник в едином реестре замечаний */
export const SITE_REMARK_TYPES: Array<{ value: RemarkSource; label: string }> = [
  { value: 'otk', label: 'Качество работ' },
  { value: 'ohs', label: 'Охрана труда' },
  { value: 'fire_safety', label: 'Пожарная безопасность' },
  { value: 'ecology', label: 'Экология' },
  { value: 'builders', label: 'Другое' },
];

export function PendingRemarksStatus({
  count,
  error,
  onRetry,
  syncing,
}: {
  count: number;
  error: string | null;
  onRetry?: () => void;
  syncing?: boolean;
}) {
  if (!count) return null;
  return (
    <Panel title="Замечания на телефоне">
      <p className="text-[14px] text-ink-2">
        {error
          ? 'Отправка не завершена. Данные сохранены на телефоне.'
          : count === 1
            ? '1 замечание ждёт связи и уйдёт само.'
            : `${count} замечания ждут связи и уйдут сами.`}
      </p>
      {error ? <ErrorBox error={new Error(error)} className="mt-2" /> : null}
      {onRetry ? (
        <Button className="mt-2" loading={syncing} onClick={onRetry}>
          Повторить отправку
        </Button>
      ) : null}
    </Panel>
  );
}

type RecoveryRow = Pick<PendingRemark, 'id' | 'serverRemarkId' | 'photos'>;

function PendingRemarkRecovery({ rows, scope }: { rows: RecoveryRow[]; scope: RemarkScope }) {
  const qc = useQueryClient();
  const downloads = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  useEffect(
    () => () => {
      for (const [url, timer] of downloads.current) {
        clearTimeout(timer);
        URL.revokeObjectURL(url);
      }
      downloads.current.clear();
    },
    [], // Компонент remount по ownerKey; cleanup закрывает все URL прежнего владельца.
  );
  const download = (photo: RecoveryRow['photos'][number]) => {
    const fresh = qc.getQueryData(meQuery.queryKey);
    if (fresh?.user.id !== scope.userId || fresh.tenant?.id !== scope.tenantId) return;
    const url = URL.createObjectURL(photo.blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = photo.name || 'photo';
    const timer = setTimeout(() => {
      URL.revokeObjectURL(url);
      downloads.current.delete(url);
    }, 1000);
    downloads.current.set(url, timer);
    try {
      document.body.append(anchor);
      anchor.click();
    } catch {
      clearTimeout(timer);
      URL.revokeObjectURL(url);
      downloads.current.delete(url);
      toast('Не удалось скачать фото. Оригинал остался на телефоне.', 'danger');
    } finally {
      anchor.remove();
    }
  };
  return (
    <Panel title="Сохранённые фото">
      <p className="text-[14px] text-ink-2">
        Скачайте оригинал, чтобы подготовить JPEG, PNG, WebP или HEIC до 10 МиБ. Файл останется на телефоне.
        Перед повторной загрузкой проверьте фото в замечании.
      </p>
      {rows.map((row) => (
        <div key={row.id} className="mt-3 flex flex-col gap-2">
          {row.serverRemarkId ? (
            <Link to="/supervision/remarks/$remarkId" params={{ remarkId: row.serverRemarkId }}>
              Открыть замечание
            </Link>
          ) : null}
          {row.photos.map((photo, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: Legacy local id могут повторяться; это stateless список восстановления без редактирования очереди.
            <div key={`${photo.id}:${index}`} className="flex flex-wrap items-center gap-2 text-[13px]">
              <span className="min-w-0 break-all">
                {photo.name} · {(photo.blob.size / 1024 / 1024).toFixed(2)} МиБ
              </span>
              <Button onClick={() => download(photo)} aria-label={`Скачать исходное фото ${photo.name}`}>
                Скачать исходное фото
              </Button>
            </div>
          ))}
        </div>
      ))}
    </Panel>
  );
}

export function MyDayPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Search;
  const day = search.day ?? todayStr();
  const days = search.days === 7 ? 7 : 1;
  useCrumbs([{ label: 'Мой день' }]);
  const [remarkFor, setRemarkFor] = useState<{ project: SiteMyDayProject; scope: RemarkScope } | null>(null);
  const [acceptFor, setAcceptFor] = useState<{
    project: SiteMyDayProject;
    delivery: SiteMyDayDelivery;
  } | null>(null);
  const q = useQuery({
    queryKey: ['site', me.tenant?.id, me.user.id, 'my-day', day, days],
    queryFn: () => api<SiteMyDay>('/site/my-day', { query: { date: day, days } }),
    enabled: Boolean(me.tenant?.staff),
  });
  const ownerKey = JSON.stringify([me.tenant?.id, me.user.id]);
  const ownerRef = useRef(ownerKey);
  ownerRef.current = ownerKey;
  const remarkKick = useRef<() => void>(() => {});
  const [remarkQueue, setRemarkQueue] = useState({
    scope: '',
    count: 0,
    error: null as string | null,
    syncing: false,
    recovery: [] as RecoveryRow[],
  });
  const pendingRemarks = remarkQueue.scope === ownerKey ? remarkQueue.count : 0;
  const pendingRemarkError = remarkQueue.scope === ownerKey ? remarkQueue.error : null;
  const [pendingDeliveries, setPendingDeliveries] = useState(0);
  // Отклонённые приёмки: причина и «Отправить снова» (тот же ключ — вторая запись не появится)
  const [failedDeliveries, setFailedDeliveries] = useState<PendingDelivery[]>([]);
  const deliveryKick = useRef<() => void>(() => {});
  useEffect(() => {
    setRemarkFor((current) =>
      current && JSON.stringify([current.scope.tenantId, current.scope.userId]) !== ownerKey ? null : current,
    );
  }, [ownerKey]);
  // Один scheduler на владельца: enqueue во время flush оставляет ещё один проход, а не теряется в Map join.
  useEffect(() => {
    if (!me.tenant?.id) return;
    const scope = { tenantId: me.tenant.id, userId: me.user.id };
    const key = JSON.stringify([scope.tenantId, scope.userId]);
    let alive = true,
      running = false,
      dirty = false;
    // Sender читает тот же cache и после ухода со страницы; unmount не означает logout.
    const isCurrentIdentity = () => {
      const fresh = qc.getQueryData(meQuery.queryKey);
      return fresh?.user.id === scope.userId && fresh.tenant?.id === scope.tenantId;
    };
    const current = () => alive && ownerRef.current === key && isCurrentIdentity();
    const run = async () => {
      if (!current()) return;
      if (running) {
        dirty = true;
        return;
      }
      running = true;
      setRemarkQueue((previous) => ({
        scope: key,
        count: previous.scope === key ? previous.count : 0,
        error: previous.scope === key ? previous.error : null,
        syncing: true,
        recovery: previous.scope === key ? previous.recovery : [],
      }));
      try {
        do {
          dirty = false;
          try {
            const store = await PendingRemarksStore.open();
            try {
              const result =
                navigator.onLine && current()
                  ? await syncPendingRemarks(store, scope, isCurrentIdentity)
                  : null;
              const rows = await store.list(scope);
              if (current()) {
                setRemarkQueue({
                  scope: key,
                  count: rows.length,
                  error: rows.find((row) => row.error)?.error ?? null,
                  syncing: true,
                  recovery: rows
                    .filter((row) => row.error && row.photos.length)
                    .map(({ id, serverRemarkId, photos }) => ({ id, serverRemarkId, photos })),
                });
                if (result?.sent) {
                  toast(
                    result.sent === 1
                      ? 'Замечание с телефона отправлено'
                      : `Отправлено замечаний: ${result.sent}`,
                    'success',
                  );
                  qc.invalidateQueries({ queryKey: ['site'] });
                }
              }
            } finally {
              store.close();
            }
          } catch {
            if (current())
              setRemarkQueue((previous) => ({
                ...previous,
                error: 'Не удалось прочитать очередь. Данные оставлены на телефоне.',
              }));
          }
        } while (dirty && current());
      } finally {
        running = false;
        if (current()) setRemarkQueue((previous) => ({ ...previous, syncing: false }));
      }
    };
    const kick = () => {
      void run();
    };
    const committed = (event: Event) => {
      const owner = (event as CustomEvent<RemarkScope>).detail;
      if (owner?.tenantId === scope.tenantId && owner.userId === scope.userId) kick();
    };
    remarkKick.current = kick;
    // Подписка до первого чтения закрывает окно между mount-flush и поздним commit старой формы.
    window.addEventListener(REMARK_COMMITTED, committed);
    window.addEventListener('online', kick);
    kick();
    return () => {
      alive = false;
      if (remarkKick.current === kick) remarkKick.current = () => {};
      window.removeEventListener(REMARK_COMMITTED, committed);
      window.removeEventListener('online', kick);
    };
  }, [me.tenant?.id, me.user.id, qc]);
  // Отчёты смены на этом телефоне уходят сами и без открытого черновика (ПМ-09)
  const [waitingReports, setWaitingReports] = useState(0);
  useEffect(() => {
    if (!me.tenant?.id) return;
    const owner = { tenantId: me.tenant.id, userId: me.user.id };
    let alive = true;
    let running = false;
    const isCurrentIdentity = () => {
      const fresh = qc.getQueryData(meQuery.queryKey);
      return fresh?.user.id === owner.userId && fresh.tenant?.id === owner.tenantId;
    };
    const tick = async () => {
      if (running || !alive) return;
      running = true;
      try {
        const store = await SiteOfflineStore.open();
        try {
          const scope = siteDeviceScope({ user: { id: owner.userId }, tenant: { id: owner.tenantId } });
          if (navigator.onLine && isCurrentIdentity()) {
            const r = await syncLocalSiteReports(store, scope, { isCurrentIdentity });
            if (alive) setWaitingReports(r.waiting);
          } else {
            const rows = await store.list(scope);
            if (alive)
              setWaitingReports(rows.filter((row) => !row.authPaused && siteReportWaiting(row)).length);
          }
        } finally {
          store.close();
        }
      } catch {
        /* хранилище или сеть недоступны — следующий проход по таймеру */
      } finally {
        running = false;
      }
    };
    void tick();
    const timer = setInterval(() => void tick(), 30_000);
    const onOnline = () => void tick();
    window.addEventListener('online', onOnline);
    return () => {
      alive = false;
      clearInterval(timer);
      window.removeEventListener('online', onOnline);
    };
  }, [me.tenant?.id, me.user.id, qc]);
  // Прежняя отдельная очередь приёмок не входит в срез замечаний.
  useEffect(() => {
    if (!me.tenant?.id) return;
    const scope = { tenantId: me.tenant.id, userId: me.user.id };
    let alive = true;
    const isCurrentIdentity = () => {
      const fresh = qc.getQueryData(meQuery.queryKey);
      return fresh?.user.id === scope.userId && fresh.tenant?.id === scope.tenantId;
    };
    const tick = async () => {
      try {
        const deliveries = await PendingDeliveriesStore.open();
        try {
          if (navigator.onLine && isCurrentIdentity()) {
            const r = await syncPendingDeliveries(deliveries, scope, isCurrentIdentity);
            if (alive && r.sent) {
              toast(
                r.sent === 1 ? 'Приёмка с телефона отправлена' : `Отправлено приёмок: ${r.sent}`,
                'success',
              );
              qc.invalidateQueries({ queryKey: ['site'] });
            }
          }
          const rows = await deliveries.list(scope);
          if (alive) {
            setPendingDeliveries(rows.filter((r) => r.state !== 'error').length);
            setFailedDeliveries(rows.filter((r) => r.state === 'error'));
          }
        } finally {
          deliveries.close();
        }
      } catch {
        /* хранилище недоступно — не мешаем экрану */
      }
    };
    void tick();
    const onOnline = () => void tick();
    deliveryKick.current = onOnline;
    window.addEventListener('online', onOnline);
    return () => {
      alive = false;
      deliveryKick.current = () => {};
      window.removeEventListener('online', onOnline);
    };
  }, [me.tenant?.id, me.user.id, qc]);
  const go = (patch: Search) => navigate({ to: '/site/day', search: { day, days, ...patch } as never });
  if (!me.tenant?.staff)
    return (
      <Page>
        <ErrorBox error={new Error('Площадка доступна сотрудникам компании')} />
      </Page>
    );
  const step = days === 7 ? 7 : 1;
  return (
    <Page>
      <PageHeader
        title="Мой день"
        subtitle={
          q.data
            ? days === 1
              ? dateLabel(q.data.from)
              : `${dateLabel(q.data.from)} — ${dateLabel(q.data.to)}`
            : 'План работ, поставки, наряды и замечания'
        }
        actions={
          <>
            <Button
              aria-label="Назад"
              icon={<ChevronLeft className="size-4" />}
              onClick={() => go({ day: shift(day, -step) })}
            >
              <span className="md:hidden">Назад</span>
            </Button>
            <Button onClick={() => go({ day: todayStr() })}>Сегодня</Button>
            <Button
              aria-label="Вперёд"
              icon={<ChevronRight className="size-4" />}
              onClick={() => go({ day: shift(day, step) })}
            >
              <span className="md:hidden">Вперёд</span>
            </Button>
            <Button variant={days === 1 ? 'primary' : 'secondary'} onClick={() => go({ days: 1 })}>
              День
            </Button>
            <Button variant={days === 7 ? 'primary' : 'secondary'} onClick={() => go({ days: 7 })}>
              Неделя
            </Button>
          </>
        }
      />
      {q.isPending ? <Spinner /> : null}
      <ErrorBox error={q.error} />
      {me.tenant ? (
        <WatchPanel
          scope={{ tenantId: me.tenant.id, userId: me.user.id }}
          projectIds={q.data?.projects.map((p) => p.projectId) ?? []}
          pending={pendingRemarks + pendingDeliveries}
        />
      ) : null}
      <PendingRemarksStatus
        count={pendingRemarks}
        error={pendingRemarkError}
        syncing={remarkQueue.syncing}
        onRetry={() => remarkKick.current()}
      />
      {remarkQueue.scope === ownerKey && remarkQueue.recovery.length && me.tenant ? (
        <PendingRemarkRecovery
          key={ownerKey}
          rows={remarkQueue.recovery}
          scope={{ tenantId: me.tenant.id, userId: me.user.id }}
        />
      ) : null}
      {waitingReports > 0 ? (
        <Panel title="Отчёты смены на телефоне">
          <p className="text-[14px] text-ink-2">
            {waitingReports === 1
              ? '1 отчёт ждёт связи и уйдёт сам.'
              : `Отчётов ждут связи: ${waitingReports}. Уйдут сами.`}
          </p>
        </Panel>
      ) : null}
      {pendingDeliveries > 0 || failedDeliveries.length > 0 ? (
        <Panel title="Приёмки на телефоне">
          {pendingDeliveries > 0 ? (
            <p className="text-[14px] text-ink-2">
              {pendingDeliveries === 1
                ? '1 приёмка ждёт связи и уйдёт сама.'
                : `${pendingDeliveries} приёмки ждут связи и уйдут сами.`}
            </p>
          ) : null}
          {failedDeliveries.map((row) => (
            <div key={row.id} className="mt-2 flex flex-col gap-2 border-t border-line pt-2">
              <p className="text-[14px] text-danger">
                {row.label ? `${row.label}: ` : ''}портал не принял приёмку —{' '}
                {row.error ?? 'причина не указана'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={async () => {
                    const store = await PendingDeliveriesStore.open();
                    try {
                      await retryPendingDelivery(store, row);
                    } finally {
                      store.close();
                    }
                    deliveryKick.current();
                  }}
                >
                  Отправить снова
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={async () => {
                    if (!window.confirm('Удалить эту приёмку с телефона? На портал она не уйдёт.')) return;
                    const store = await PendingDeliveriesStore.open();
                    try {
                      await store.remove(row.id);
                    } finally {
                      store.close();
                    }
                    deliveryKick.current();
                  }}
                >
                  Удалить с телефона
                </Button>
              </div>
            </div>
          ))}
        </Panel>
      ) : null}
      {q.data ? <DeadlineBanner data={q.data} day={day} /> : null}
      {q.data && !q.data.projects.length ? (
        <EmptyState title="На этот срок ничего не запланировано">
          Здесь появятся выданные вам пакеты работ, поставки по договорам, действующие наряды-допуски и
          замечания, которые нужно устранить.
        </EmptyState>
      ) : null}
      <div className="flex flex-col gap-5">
        {q.data?.projects.map((p) => (
          <ProjectDay
            key={p.projectId}
            p={p}
            day={q.data.from}
            deadline={q.data.deadline}
            onRemark={() =>
              setRemarkFor({ project: p, scope: { tenantId: me.tenant!.id, userId: me.user.id } })
            }
            onAccept={(delivery) => setAcceptFor({ project: p, delivery })}
          />
        ))}
      </div>
      {remarkFor && JSON.stringify([remarkFor.scope.tenantId, remarkFor.scope.userId]) === ownerKey ? (
        <RemarkDialog
          key={`${remarkFor.scope.tenantId}:${remarkFor.scope.userId}:${remarkFor.project.projectId}`}
          project={remarkFor.project}
          scope={remarkFor.scope}
          onClose={() => setRemarkFor(null)}
        />
      ) : null}
      {acceptFor ? (
        <DeliveryAcceptDialog
          project={acceptFor.project}
          delivery={acceptFor.delivery}
          onClose={() => setAcceptFor(null)}
          onQueued={() => setPendingDeliveries((n) => n + 1)}
        />
      ) : null}
    </Page>
  );
}

const SUBMITTED = new Set(['submitted', 'verified', 'accepted']);

function DeadlineBanner({ data, day }: { data: SiteMyDay; day: string }) {
  const d = data.deadline;
  const hour = `${String(d.hour).padStart(2, '0')}:00`;
  const isToday = day === d.today && data.days === 1;
  if (!isToday) return null;
  const missing = data.projects.some((p) =>
    p.packages.some((pk) => !p.reports.some((r) => r.packageId === pk.packageId && SUBMITTED.has(r.state))),
  );
  return (
    <Panel
      title={d.pastDeadline ? 'Срок сдачи отчёта прошёл' : 'Срок сдачи отчёта смены'}
      meta={`${hour} · ${d.timezone}`}
      actions={
        missing ? (
          <Link to="/site/missing" search={{ day: d.today } as never}>
            <Button>Кто не сдал</Button>
          </Link>
        ) : null
      }
    >
      <p className="text-[14px] text-ink-2">
        {d.pastDeadline
          ? missing
            ? 'Отчёт за сегодня ещё не сдан по одному или нескольким участкам — он в «Ждёт меня» у прораба и у руководителя проекта.'
            : 'Все участки с выданным пакетом сдали отчёт на проверку.'
          : `Сдайте отчёт смены до ${hour}. После срока несданные участки появятся в «Ждёт меня».`}
      </p>
    </Panel>
  );
}

function ProjectDay({
  p,
  day,
  deadline,
  onRemark,
  onAccept,
}: {
  p: SiteMyDayProject;
  day: string;
  deadline: SiteMyDay['deadline'];
  onRemark: () => void;
  onAccept: (d: SiteMyDayDelivery) => void;
}) {
  return (
    <Panel
      title={`${p.code} · ${p.name}`}
      actions={
        <>
          <Link to="/site/summary" search={{ project: p.projectId, day } as never}>
            <Button>Сводка дня</Button>
          </Link>
          {p.canReport ? (
            <Link to="/site" search={{ project: p.projectId, day } as never}>
              <Button variant="primary" icon={<ClipboardList className="size-4" />}>
                Отчёт смены
              </Button>
            </Link>
          ) : null}
          {p.canIssueRemark ? (
            <Button icon={<Camera className="size-4" />} onClick={onRemark}>
              Замечание с фото
            </Button>
          ) : null}
        </>
      }
      bodyClassName="p-0"
    >
      <div className="divide-y divide-line">
        <Section title="Работы">
          {p.packages.length ? (
            p.packages.map((pk) => {
              const submitted = p.reports.some(
                (r) => r.packageId === pk.packageId && r.reportDay === day && SUBMITTED.has(r.state),
              );
              const showDeadline = day === deadline.today;
              return (
                <div key={pk.packageId} className="py-2">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="text-[14px] font-medium text-ink">{pk.name}</span>
                    <span className="text-[12px] text-ink-2">
                      {pk.dateFrom} — {pk.dateTo}
                      {pk.contractorName ? ` · ${pk.contractorName}` : ''}
                      {pk.foremanName ? ` · ${pk.foremanName}` : ''}
                    </span>
                    {showDeadline ? (
                      <Badge tone={submitted ? 'success' : deadline.pastDeadline ? 'danger' : 'warning'}>
                        {submitted
                          ? 'Отчёт сдан'
                          : deadline.pastDeadline
                            ? 'Не сдан'
                            : `До ${String(deadline.hour).padStart(2, '0')}:00`}
                      </Badge>
                    ) : null}
                  </div>
                  <ul className="mt-1 flex flex-col gap-1.5">
                    {pk.lines.map((l) => (
                      <li key={l.activityId} className="rounded-md bg-surface-2 px-3 py-2 text-[13px]">
                        <div className="flex flex-wrap justify-between gap-x-3">
                          <span className="text-ink">{l.name}</span>
                          <span className="text-ink-2">
                            {qty(l.actualQuantity)} из {qty(l.totalQuantity)} {l.unit ?? ''}
                          </span>
                        </div>
                        {l.dayQuantity ? (
                          <div className="mt-0.5 font-medium text-ink">
                            На день: {qty(l.dayQuantity).replace('.', ',')} {l.unit ?? ''}
                          </div>
                        ) : null}
                        {l.resources.length ? (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {l.resources.map((r) => (
                              <Badge
                                key={`${r.kind}:${r.name}`}
                                tone={r.kind === 'equipment' ? 'info' : 'neutral'}
                              >
                                {r.name} × {r.units}
                              </Badge>
                            ))}
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          ) : (
            <Empty>Пакетов работ на этот срок нет</Empty>
          )}
        </Section>
        {p.deliveries ? (
          <Section title="Поставки к приёмке">
            {p.deliveries.length ? (
              p.deliveries.map((d) => (
                <div
                  key={d.id}
                  className="flex flex-wrap items-center justify-between gap-2 py-1.5 text-[13px]"
                >
                  <div className="min-w-0">
                    <span className="font-medium text-ink">{d.plannedDate}</span> ·{' '}
                    {d.description ?? 'Поставка'}
                    {d.quantity !== null ? `, ${d.quantity}` : ''} · договор {d.contractNumber}
                    {d.supplierName ? `, ${d.supplierName}` : ''}
                    {d.acceptance ? (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        <Badge tone="success">Принято {d.acceptance.receivedQuantity}</Badge>
                        {d.acceptance.passportPresent ? (
                          <Badge tone="neutral">Паспорт есть</Badge>
                        ) : (
                          <Badge tone="warning">Без паспорта</Badge>
                        )}
                        {d.acceptance.remarkId ? (
                          <Link
                            to="/supervision/remarks/$remarkId"
                            params={{ remarkId: d.acceptance.remarkId }}
                            className="inline-flex min-h-11 items-center underline"
                          >
                            Замечание поставщику
                          </Link>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  {p.canReport && !d.acceptance ? (
                    <Button size="sm" icon={<PackageCheck className="size-4" />} onClick={() => onAccept(d)}>
                      Принять
                    </Button>
                  ) : null}
                </div>
              ))
            ) : (
              <Empty>Поставок по графику договоров нет</Empty>
            )}
          </Section>
        ) : null}
        {p.permits ? (
          <Section title="Наряды-допуски">
            {p.permits.length ? (
              p.permits.map((n) => (
                <div key={n.id} className="py-1.5 text-[13px]">
                  <Link
                    to="/qhse/permits/$permitId"
                    params={{ permitId: n.id }}
                    className="inline-flex min-h-11 items-center font-medium underline"
                  >
                    {n.number}
                  </Link>{' '}
                  · {PERMIT_KINDS[n.kind as PermitKind]?.title ?? n.kind} · {n.title}
                  {n.activityName ? ` · ${n.activityName}` : ''}
                  {n.status === 'suspended' ? (
                    <>
                      {' '}
                      <Badge tone="danger">{PERMIT_STATUSES.suspended}</Badge>
                    </>
                  ) : null}
                  <div className="text-[12px] text-ink-2">
                    {timeLabel(n.validFrom)} — {timeLabel(n.validUntil)}
                    {n.place ? ` · ${n.place}` : ''}
                  </div>
                </div>
              ))
            ) : (
              <Empty>Нарядов на этот срок нет</Empty>
            )}
          </Section>
        ) : null}
        {p.remarks ? (
          <Section title="Замечания к устранению">
            {p.remarks.length ? (
              p.remarks.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-2 py-1.5 text-[13px]">
                  <Link
                    to="/supervision/remarks/$remarkId"
                    params={{ remarkId: r.id }}
                    className="inline-flex min-h-11 items-center font-medium underline"
                  >
                    {r.number}
                  </Link>
                  <span>{r.title}</span>
                  <Badge tone={r.overdue ? 'danger' : 'neutral'}>до {r.dueDate}</Badge>
                  {r.stopWork ? <Badge tone="danger">Работы остановлены</Badge> : null}
                  {r.mine ? <Badge tone="accent">Моё</Badge> : null}
                </div>
              ))
            ) : (
              <Empty>Открытых замечаний нет</Empty>
            )}
          </Section>
        ) : null}
        {p.reports.length ? (
          <Section title="Мои отчёты смены">
            {p.reports.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-2 py-1.5 text-[13px]">
                <Link
                  to="/site"
                  search={{ project: p.projectId, day: r.reportDay, report: r.id } as never}
                  className="inline-flex min-h-11 items-center font-medium underline"
                >
                  {r.reportDay}
                </Link>
                <Badge>{SITE_STATES[r.state as keyof typeof SITE_STATES] ?? r.state}</Badge>
              </div>
            ))}
          </Section>
        ) : null}
      </div>
    </Panel>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="px-5 py-3">
      <h3 className="mb-1 text-[13px] font-semibold tracking-wide text-ink-2 uppercase">{title}</h3>
      {children}
    </section>
  );
}
const Empty = ({ children }: { children: React.ReactNode }) => (
  <p className="py-1 text-[13px] text-ink-3">{children}</p>
);

/** Приёмка поставки на площадке: количество, накладная, паспорт, фото (ПР-08); без связи — на телефон (МП-02) */
function DeliveryAcceptDialog({
  project,
  delivery,
  onClose,
  onQueued,
}: {
  project: SiteMyDayProject;
  delivery: SiteMyDayDelivery;
  onClose: () => void;
  onQueued?: () => void;
}) {
  const me = useMe();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [f, setF] = useState({
    receivedQuantity: delivery.quantity != null ? String(delivery.quantity) : '',
    waybillNumber: '',
    passportPresent: true,
    note: '',
  });
  const [photos, setPhotos] = useState<File[]>([]);
  const send = useMutation({
    // Без сети React Query по умолчанию ставит мутацию на паузу, а приёмка как раз должна сохраниться на телефон
    networkMode: 'always',
    mutationFn: async () => {
      const qty = Number(f.receivedQuantity.replace(',', '.'));
      if (!Number.isFinite(qty) || qty <= 0) throw new Error('Укажите принятое количество');
      if (!photos.length) throw new Error('Добавьте хотя бы одно фото');
      if (!f.passportPresent && !project.canIssueRemark)
        throw new Error('Без паспорта нужно право выдавать замечания поставщику');
      const body = {
        receivedQuantity: qty,
        waybillNumber: f.waybillNumber.trim() || null,
        passportPresent: f.passportPresent,
        note: f.note.trim() || null,
      };
      if (!me.tenant?.id) throw new Error('Выберите компанию');
      const scope = { tenantId: me.tenant.id, userId: me.user.id };
      // Сначала запись на телефон, потом отправка той же очередью: один ключ повтора и при связи, и без неё
      const store = await PendingDeliveriesStore.open();
      try {
        const row = await enqueuePendingDelivery(
          store,
          scope,
          project.projectId,
          delivery.id,
          body,
          photos.map((file, i) => ({
            id: `local-${i}-${file.name}`,
            name: file.name,
            type: file.type || 'image/jpeg',
            blob: file,
          })),
          Date.now(),
          `${delivery.description ?? 'Поставка'} · договор ${delivery.contractNumber}`,
        );
        const result = navigator.onLine ? await syncPendingDeliveries(store, scope) : null;
        const done = result?.accepted?.[row.id];
        if (done) return done;
        const left = (await store.list(scope)).find((r) => r.id === row.id);
        if (left?.state === 'error') {
          // Портал отказал сразу — показываем причину здесь, на телефоне запись не держим
          await store.remove(row.id);
          throw new Error(left.error ?? 'Портал не принял приёмку');
        }
      } finally {
        store.close();
      }
      onQueued?.();
      toast('Нет связи — приёмка сохранена на телефоне и уйдёт сама', 'info');
      onClose();
      return null;
    },
    onSuccess: (r) => {
      if (!r) return;
      qc.invalidateQueries({ queryKey: ['site'] });
      toast(
        r.remarkId
          ? 'Поставка принята без паспорта — создано замечание поставщику'
          : 'Поставка принята на площадке',
        r.remarkId ? 'info' : 'success',
      );
      onClose();
    },
  });
  const qtyOk = Number(f.receivedQuantity.replace(',', '.')) > 0;
  const ready = qtyOk && photos.length > 0 && (f.passportPresent || project.canIssueRemark);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Приёмка материала"
      description={`${delivery.description ?? 'Поставка'} · договор ${delivery.contractNumber}`}
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" disabled={!ready} loading={send.isPending} onClick={() => send.mutate()}>
            Принять
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ErrorBox error={send.error} />
        <Field label="Принято, количество" htmlFor="da-qty">
          <Input
            id="da-qty"
            inputMode="decimal"
            className="!h-11"
            value={f.receivedQuantity}
            onChange={(e) => setF({ ...f, receivedQuantity: e.target.value })}
          />
        </Field>
        <Field label="Номер накладной" htmlFor="da-waybill">
          <Input
            id="da-waybill"
            className="!h-11"
            value={f.waybillNumber}
            maxLength={120}
            onChange={(e) => setF({ ...f, waybillNumber: e.target.value })}
          />
        </Field>
        <Checkbox
          className="min-h-11"
          label="Паспорт / сертификат качества есть"
          checked={f.passportPresent}
          onChange={(e) => setF({ ...f, passportPresent: e.target.checked })}
        />
        {!f.passportPresent ? (
          <p className="text-[13px] text-ink-2">
            Без паспорта приёмка всё равно сохраняется, и поставщику автоматически выдаётся замечание
            {delivery.supplierName ? ` (${delivery.supplierName})` : ''}.
          </p>
        ) : null}
        <Field label="Комментарий" htmlFor="da-note">
          <Textarea id="da-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        </Field>
        <Field label="Фото" hint="Снимите накладную, упаковку или паспорт на телефон">
          <input
            ref={input}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => setPhotos((x) => [...x, ...Array.from(e.target.files ?? [])])}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button icon={<Camera className="size-4" />} onClick={() => input.current?.click()}>
              Добавить фото
            </Button>
          </div>
          {photos.map((p) => (
            <div
              key={`${p.name}:${p.size}:${p.lastModified}`}
              className="flex items-center justify-between gap-2 text-[13px]"
            >
              <span className="min-w-0 truncate">{p.name}</span>
              <Button
                size="sm"
                aria-label={`Убрать ${p.name}`}
                onClick={() => setPhotos((x) => x.filter((y) => y !== p))}
              >
                Убрать
              </Button>
            </div>
          ))}
        </Field>
        <p className="text-[12px] text-ink-3">
          Поступление на склад по этому договору оформите отдельно в разделе «Склад» — здесь фиксируется факт
          приёмки на площадке.
        </p>
      </div>
    </Dialog>
  );
}

/** Замечание с площадки: тип, работа, место, ответственный, срок и фото «до» (ПР-07); без связи — на телефон (МП-02) */
function RemarkDialog({
  project,
  scope,
  onClose,
}: {
  project: SiteMyDayProject;
  scope: RemarkScope;
  onClose: () => void;
}) {
  const me = useMe();
  const busy = useRef(false);
  const operationId = useRef<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const input = useRef<HTMLInputElement>(null);
  const [f, setF] = useState({
    source: 'otk' as RemarkSource,
    kind: 'remark' as RemarkKind,
    title: '',
    description: '',
    activityId: '',
    sheetId: '',
    place: '',
    assigneeId: '',
    dueDate: shift(todayStr(), 3),
    stopWork: false,
  });
  const [photos, setPhotos] = useState<Array<{ id: string; file: File }>>([]);
  const [marking, setMarking] = useState<string | null>(null);
  const works = project.packages.flatMap((pk) => pk.lines.map((l) => ({ id: l.activityId, name: l.name })));
  const unique = [...new Map(works.map((w) => [w.id, w])).values()];
  const people = useQuery({
    queryKey: ['supervision', scope.tenantId, scope.userId, 'people', project.projectId],
    queryFn: () =>
      api<Array<{ id: string; fullName: string; external: boolean }>>('/supervision/people', {
        query: { projectId: project.projectId, perm: 'respond' },
        expectedIdentity: scope,
      }),
  });
  const photoLinks = useQuery({
    queryKey: ['site', scope.tenantId, scope.userId, 'photo-links', project.projectId],
    queryFn: () =>
      api<SitePhotoLinks>(`/site/projects/${project.projectId}/photo-links`, { expectedIdentity: scope }),
    retry: false,
  });
  const send = useMutation({
    // Замечание пишется на телефон до первого запроса: без сети мутация не должна вставать на паузу
    networkMode: 'always',
    mutationFn: async (clientOperationId: string) => {
      if (me.tenant?.id !== scope.tenantId || me.user.id !== scope.userId)
        throw new Error('Сессия изменилась. Откройте замечание в исходной компании.');
      for (const photo of photos) {
        const error = remarkPhotoError({ name: photo.file.name, blob: photo.file });
        if (error) throw new Error(error);
      }
      let store: PendingRemarksStore | undefined;
      try {
        store = await PendingRemarksStore.open();
        await enqueuePendingRemark(
          store,
          scope,
          {
            projectId: project.projectId,
            source: f.source,
            kind: f.kind,
            title: f.title.trim(),
            description: f.description.trim() || null,
            activityId: f.activityId || null,
            sheetId: f.sheetId || null,
            place: f.place.trim() || null,
            assigneeId: f.assigneeId || null,
            dueDate: f.dueDate,
            stopWork: f.stopWork,
          },
          photos.map((p) => ({
            id: p.id,
            name: p.file.name || 'photo.jpg',
            type: p.file.type || 'image/jpeg',
            blob: p.file,
          })),
          Date.now(),
          clientOperationId,
        );
      } catch {
        throw new Error(
          'Не удалось сохранить замечание на телефоне. Введённые данные оставлены в форме. Освободите место и повторите.',
        );
      } finally {
        store?.close();
      }
      // Только scope, после commit; не callback закрытой страницы и не тело/Blob.
      window.dispatchEvent(new CustomEvent(REMARK_COMMITTED, { detail: { ...scope } }));
    },
    onSuccess: () => {
      // Persist старого владельца завершается, но его поздний callback не закрывает новую форму/сессию.
      if (!mounted.current) return;
      toast('Замечание сохранено на телефоне — отправка продолжится при наличии связи', 'info');
      onClose();
    },
  });
  const submit = () => {
    // Синхронно, до первого await и следующего render: два клика — один intent и UUID.
    if (busy.current) return;
    busy.current = true;
    operationId.current ??= crypto.randomUUID();
    void send
      .mutateAsync(operationId.current)
      .finally(() => {
        busy.current = false;
      })
      .catch(() => {});
  };
  const close = () => {
    if (!busy.current) onClose();
  };
  const ready = f.title.trim().length >= 3 && Boolean(f.dueDate);
  const markingPhoto = marking ? photos.find((p) => p.id === marking) : null;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && close()}
      title="Замечание с фото"
      description={`${project.code} · ${project.name}`}
      footer={
        <>
          <Button onClick={close} disabled={send.isPending}>
            Отмена
          </Button>
          <Button variant="primary" disabled={!ready} loading={send.isPending} onClick={submit}>
            Выдать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ErrorBox error={send.error} />
        <Field label="Тип" htmlFor="rm-source">
          <Select
            id="rm-source"
            value={f.source}
            onChange={(e) => setF({ ...f, source: e.target.value as RemarkSource })}
          >
            {SITE_REMARK_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Вид" htmlFor="rm-kind">
          <Select
            id="rm-kind"
            value={f.kind}
            onChange={(e) => setF({ ...f, kind: e.target.value as RemarkKind })}
          >
            {(Object.keys(REMARK_KINDS) as RemarkKind[]).map((k) => (
              <option key={k} value={k}>
                {REMARK_KINDS[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Что не так" htmlFor="rm-title">
          <Input
            id="rm-title"
            value={f.title}
            maxLength={300}
            onChange={(e) => setF({ ...f, title: e.target.value })}
          />
        </Field>
        <Field label="Подробности" htmlFor="rm-desc">
          <Textarea
            id="rm-desc"
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </Field>
        <Field
          label="Фото"
          hint="Снимите на телефон или выберите из галереи. До выдачи можно обвести важное красным. Фото «после» добавляет тот, кто устраняет."
        >
          <input
            ref={input}
            type="file"
            accept={REMARK_PHOTO_EXTENSIONS.map((extension) => `.${extension}`).join(',')}
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) =>
              setPhotos((x) => [
                ...x,
                ...Array.from(e.target.files ?? []).map((file) => ({ id: crypto.randomUUID(), file })),
              ])
            }
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button icon={<Camera className="size-4" />} onClick={() => input.current?.click()}>
              Добавить фото
            </Button>
          </div>
          {photos.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 text-[13px]">
              <span className="min-w-0 truncate">{p.file.name}</span>
              <div className="flex shrink-0 gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Пометить ${p.file.name}`}
                  icon={<Pencil className="size-4" />}
                  onClick={() => setMarking(p.id)}
                />
                <Button
                  size="sm"
                  aria-label={`Убрать ${p.file.name}`}
                  onClick={() => setPhotos((x) => x.filter((y) => y.id !== p.id))}
                >
                  Убрать
                </Button>
              </div>
            </div>
          ))}
          {markingPhoto ? (
            <PhotoMarkupDialog
              blob={markingPhoto.file}
              onClose={() => setMarking(null)}
              onSave={(prepared) => {
                const base = markingPhoto.file.name.replace(/\.[^.]+$/, '') || 'photo';
                const next = new File([prepared.blob], `${base}-marked.jpg`, { type: 'image/jpeg' });
                setPhotos((list) => list.map((row) => (row.id === marking ? { ...row, file: next } : row)));
                setMarking(null);
              }}
            />
          ) : null}
        </Field>
        <Field label="Работа графика" htmlFor="rm-act">
          <Select
            id="rm-act"
            value={f.activityId}
            onChange={(e) => setF({ ...f, activityId: e.target.value })}
          >
            <option value="">Не привязано</option>
            {unique.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Лист ПСД" htmlFor="rm-sheet" hint="Необязательно: лист действующего комплекта проекта">
          <Select
            id="rm-sheet"
            className="!h-11"
            value={f.sheetId}
            onChange={(e) => setF({ ...f, sheetId: e.target.value })}
          >
            <option value="">Не указан</option>
            {(photoLinks.data?.sheets ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Место" htmlFor="rm-place" hint="Оси, пикет или отметка">
          <Input
            id="rm-place"
            value={f.place}
            maxLength={300}
            onChange={(e) => setF({ ...f, place: e.target.value })}
          />
        </Field>
        <Field label="Ответственный" htmlFor="rm-who">
          <Select
            id="rm-who"
            value={f.assigneeId}
            onChange={(e) => setF({ ...f, assigneeId: e.target.value })}
          >
            <option value="">Не назначен</option>
            {people.data?.map((u) => (
              <option key={u.id} value={u.id}>
                {u.fullName}
                {u.external ? ' (подрядчик)' : ''}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Срок устранения" htmlFor="rm-due">
          <Input
            id="rm-due"
            type="date"
            value={f.dueDate}
            onChange={(e) => setF({ ...f, dueDate: e.target.value })}
          />
        </Field>
        <Checkbox
          className="min-h-11 items-center"
          label="Остановить работы до устранения"
          checked={f.stopWork}
          onChange={(e) => setF({ ...f, stopWork: e.target.checked })}
        />
        <p className="text-[12px] text-ink-2">
          Замечание попадёт в общий реестр. Закрывает его тот, кто выдал, после проверки фото «после».
        </p>
      </div>
    </Dialog>
  );
}
